import { useCallback, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'app/providers/Store';
import {
  engineSelectors,
  getEngines,
  getEngineById,
  createEngine,
  updateEngine,
  deleteEngine,
  EngineListItem,
} from 'entities/engine';
import { userSelectors } from 'entities/user';
import { EngineRequestDto } from 'shared/api';
import { useGlobalSpinner } from 'shared/hooks';
import { message } from 'antd';
import dayjs from 'dayjs';

export type EngineStatusInfo = {
  status: string;
  color: string;
  /** Недель до ТО. null - даты не заданы либо ТО уже просрочено. */
  weeksUntilMaintenance: number | null;
  /** Недель просрочки. null - ТО не просрочено. */
  weeksOverdue: number | null;
  progressBarPercent: number;
};

export const useEngines = () => {
  const dispatch = useAppDispatch();
  const engines = useAppSelector(engineSelectors.getEngines);
  const isLoading = useAppSelector(engineSelectors.getEnginesLoading);
  const engineDetail = useAppSelector(engineSelectors.getEngineDetail);
  const engineDetailLoading = useAppSelector(engineSelectors.getEngineDetailLoading);
  const hasAdminRole = useAppSelector(userSelectors.hasAdminRole);
  const user = useAppSelector(userSelectors.getUser);
  const { showSpinner, hideSpinner } = useGlobalSpinner();

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const loadEngines = useCallback(() => {
    showSpinner('Загрузка двигателей...');
    const params = hasAdminRole ? {} : (user?.id ? { userId: user.id } : {});
    dispatch(getEngines(params)).finally(() => {
      hideSpinner();
    });
  }, [dispatch, showSpinner, hideSpinner, hasAdminRole]);

  const createNewEngine = useCallback(async (data: EngineRequestDto) => {
    try {
      showSpinner('Создание двигателя...');
      await dispatch(createEngine(data)).unwrap();
      loadEngines();
      message.success('Двигатель успешно создан');
      return { success: true };
    } catch (error) {
      message.error('Ошибка при создании двигателя');
      return { success: false, error };
    } finally {
      hideSpinner();
    }
  }, [dispatch, loadEngines, showSpinner, hideSpinner]);

  const updateExistingEngine = useCallback(async (id: number, data: EngineRequestDto) => {
    try {
      showSpinner('Обновление двигателя...');
      await dispatch(updateEngine({ id, data })).unwrap();
      loadEngines();
      message.success('Двигатель успешно обновлен');
      return { success: true };
    } catch (error) {
      message.error('Ошибка при обновлении двигателя');
      return { success: false, error };
    } finally {
      hideSpinner();
    }
  }, [dispatch, loadEngines, showSpinner, hideSpinner]);

  const deleteEngineById = useCallback(async (id: number) => {
    try {
      showSpinner('Удаление двигателя...');
      await dispatch(deleteEngine(id)).unwrap();
      loadEngines();
      message.success('Двигатель успешно удален');
      return { success: true };
    } catch (error) {
      message.error('Ошибка при удалении двигателя');
      return { success: false, error };
    } finally {
      hideSpinner();
    }
  }, [dispatch, loadEngines, showSpinner, hideSpinner]);

  const loadEngineDetail = useCallback(async (id: number) => {
    try {
      showSpinner('Загрузка информации о двигателе...');
      await dispatch(getEngineById(id)).unwrap();
    } finally {
      hideSpinner();
    }
  }, [dispatch]);

  const HOURS_PER_WEEK = 24 * 7;

  const getStatusInfo = useCallback((engine: EngineListItem): EngineStatusInfo => {
    const now = dayjs();
    const lastMaintenance = engine.lastMaintenance ? dayjs(engine.lastMaintenance) : null;

    // ТО выполняется во время останова машины, поэтому считаем до планируемого
    // останова, если он задан. Расчётная дата (последнее ТО + межремонтный интервал)
    // остаётся запасным вариантом: она предполагает работу 24/7 и потому оптимистична.
    const nextMaintenance = engine.plannedDateOfStop
      ? dayjs(engine.plannedDateOfStop)
      : (engine.nextMaintenance ? dayjs(engine.nextMaintenance) : null);

    // Даты не заданы - срок неизвестен. Возвращаем null, а не 0:
    // ноль недель означал бы "ТО прямо сейчас", это разные вещи.
    if (!lastMaintenance?.isValid() || !nextMaintenance?.isValid()) {
      return {
        status: 'Норма',
        color: '#52c41a',
        weeksUntilMaintenance: null,
        weeksOverdue: null,
        progressBarPercent: 0,
      };
    }
    const totalPeriod = nextMaintenance.diff(lastMaintenance, 'hour');
    const elapsedTime = now.diff(lastMaintenance, 'hour');

    const totalWeeks = Math.floor(totalPeriod / HOURS_PER_WEEK);
    const elapsedWeeks = Math.floor(elapsedTime / HOURS_PER_WEEK);
    const progressBarPercent = totalWeeks > 0
      ? Math.min(100, Math.max(0, (elapsedWeeks / totalWeeks) * 100))
      : (elapsedTime > 0 ? 100 : 0);

    const hoursUntilMaintenance = nextMaintenance.diff(now, 'hour');

    if (hoursUntilMaintenance < 0) {
      return {
        status: 'Критично',
        color: '#ff4d4f',
        weeksUntilMaintenance: null,
        weeksOverdue: Math.floor(-hoursUntilMaintenance / HOURS_PER_WEEK),
        progressBarPercent: 100,
      };
    }

    const weeksUntilMaintenance = Math.floor(hoursUntilMaintenance / HOURS_PER_WEEK);

    if (hoursUntilMaintenance <= 30 * 24) {
      return {
        status: 'Внимание',
        color: '#faad14',
        weeksUntilMaintenance,
        weeksOverdue: null,
        progressBarPercent,
      };
    }

    return {
      status: 'Норма',
      color: '#52c41a',
      weeksUntilMaintenance,
      weeksOverdue: null,
      progressBarPercent,
    };
  }, []);

  const formatDate = useCallback((dateString: string) => {
    if (!dateString) return 'Не указано';
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU');
  }, []);

  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const handleFilter = useCallback((filter: string) => {
    setStatusFilter(filter);
  }, []);

  const filteredEngines = useMemo(() => {
    let filtered = engines;

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((engine) => engine.orderCode?.toLowerCase().includes(query)
        || engine.serialNumber?.toLowerCase().includes(query));
    }

    if (statusFilter) {
      filtered = filtered.filter((engine) => {
        const statusInfo = getStatusInfo(engine);
        return statusInfo.status.toLowerCase() === statusFilter;
      });
    }

    return filtered;
  }, [engines, searchQuery, statusFilter, getStatusInfo]);

  return useMemo(() => ({
    engines: filteredEngines,
    allEngines: engines,
    isLoading,
    loadEngines,
    createNewEngine,
    updateExistingEngine,
    deleteEngineById,
    engineDetail,
    engineDetailLoading,
    loadEngineDetail,
    getStatusInfo,
    formatDate,
    handleSearch,
    handleFilter,
    searchQuery,
    statusFilter,
  }), [
    filteredEngines,
    engines,
    isLoading,
    loadEngines,
    createNewEngine,
    updateExistingEngine,
    deleteEngineById,
    engineDetail,
    engineDetailLoading,
    loadEngineDetail,
    getStatusInfo,
    formatDate,
    handleSearch,
    handleFilter,
    searchQuery,
    statusFilter,
  ]);
};
